import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LinkIcon from '@/styles/Icons/Link';
import { CommentPreview } from '@/components/ui/shared/CommentPreview';
import { getAvatar } from '@/utils/helpers/profiles';
import { truncateContent } from '@/utils/helpers/strings';
import { formatRelativeDate } from '@/utils/helpers/formatTimeDate';
import type { DiscussionComment } from './types';
import css from './DiscussionsPage.module.css';

const PAGE_SIZE = 3;

const LABELS = {
  showPrev: 'показать предыдущие',
  showNext: 'показать следующие'
};

function computeInitialWindow(sorted: DiscussionComment[]): [number, number] {
  const total = sorted.length;
  if (total <= PAGE_SIZE) return [0, total];
  const unreadCount = sorted.filter(c => c.state === 'new').length;
  if (unreadCount <= PAGE_SIZE) return [total - PAGE_SIZE, total];
  const firstUnread = Math.max(0, sorted.findIndex(c => c.state === 'new'));
  return [firstUnread, Math.min(total, firstUnread + PAGE_SIZE)];
}

interface Props {
  comments: DiscussionComment[];
  blogSlug: string;
  entryId: string;
}

export function DiscussionComments({ comments, blogSlug, entryId }: Props) {
  const sorted = useMemo(
    () => [...comments].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [comments]
  );

  const total = sorted.length;

  const [startIdx, setStartIdx] = useState(() => computeInitialWindow(sorted)[0]);
  const [endIdx, setEndIdx] = useState(() => computeInitialWindow(sorted)[1]);

  const showPrevBtn = startIdx > 0;
  const showNextBtn = endIdx < total;
  const visible = sorted.slice(startIdx, endIdx);

  return (
    <div className={css.expandedComments}>
      {showPrevBtn && (
        <div className={css.loadMoreRow}>
          <div className={css.commentExpandCol} />
          <button className={css.loadMoreBtn} onClick={() => setStartIdx(i => Math.max(0, i - PAGE_SIZE))}>
            {LABELS.showPrev}
          </button>
        </div>
      )}

      {visible.map(comment => {
        const isUnread = comment.state === 'new';
        const profile = comment.profile;
        const avatar = getAvatar({ settings: profile?.settings });
        const commentHref = `/blog/${blogSlug}/${entryId}#${comment.id}`;
        const dateLabel = formatRelativeDate(comment.createdAt);
        const commentText = truncateContent(comment.content);

        return (
          <div key={comment.id} className={css.commentRow}>
            <div className={css.commentExpandCol}>
              <div className={isUnread ? css.commentUnread : css.commentRead} />
            </div>

            <div className={css.commentContent}>
              <CommentPreview avatarUrl={avatar} nickname={profile?.nickname} text={commentText} />
            </div>

            <Link to={commentHref} className={css.commentLinkCol} title="к комментарию">
              <LinkIcon className={css.commentLinkIcon} />
            </Link>

            <div className={css.commentDateCol}>{dateLabel}</div>
          </div>
        );
      })}

      {showNextBtn && (
        <div className={css.loadMoreRow}>
          <div className={css.commentExpandCol} />
          <button className={css.loadMoreBtn} onClick={() => setEndIdx(i => Math.min(total, i + PAGE_SIZE))}>
            {LABELS.showNext}
          </button>
        </div>
      )}
    </div>
  );
}
